const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL;

if (!configuredBackendUrl) {
  throw new Error("REACT_APP_BACKEND_URL fehlt");
}

const configuredOrigin = new URL(configuredBackendUrl).origin;
const API_HEALTH_PATH = "/api/";
const RESOLVED_ORIGIN_STORAGE_KEY = "inspection-api-origin";

const uniqueOrigins = (origins) => [...new Set(origins.filter(Boolean))];

const getWindowOrigin = () =>
  typeof window !== "undefined" ? window.location.origin : null;

const readStoredOrigin = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage.getItem(RESOLVED_ORIGIN_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const storeResolvedOrigin = (origin) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(RESOLVED_ORIGIN_STORAGE_KEY, origin);
  } catch (error) {
    return;
  }
};

const getOriginCandidates = () => {
  const currentOrigin = getWindowOrigin();
  const storedOrigin = readStoredOrigin();

  if (currentOrigin && currentOrigin !== configuredOrigin) {
    return uniqueOrigins([currentOrigin, storedOrigin, configuredOrigin]);
  }

  return uniqueOrigins([storedOrigin, currentOrigin, configuredOrigin]);
};

const buildApiUrl = (origin, path) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const effectivePath = normalizedPath.startsWith("/api")
    ? normalizedPath
    : `/api${normalizedPath}`;

  return `${origin}${effectivePath}`;
};

const parseJsonSafely = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const readResponseTextSafely = async (response) => {
  if (!response || response.bodyUsed) {
    return null;
  }

  try {
    return await response.text();
  } catch (error) {
    return null;
  }
};

const createApiError = (message, details = {}) => {
  const error = new Error(message);
  Object.assign(error, details);
  return error;
};

const createFriendlyApiError = (error, attemptedOrigins = []) => {
  const message = error?.message || "API-Fehler";
  const currentOrigin = getWindowOrigin();
  const hasCrossDomainSetup = currentOrigin && currentOrigin !== configuredOrigin;
  const isTransportError =
    message.includes("(0)") ||
    message.includes("Failed to fetch") ||
    message.includes("Network request failed");

  if (isTransportError && hasCrossDomainSetup) {
    return createApiError(
      "API auf dieser Domain nicht erreichbar. Bitte Seite neu laden. Falls der Fehler bleibt, ist wahrscheinlich noch ein älterer Frontend-Build aktiv.",
      { attemptedOrigins }
    );
  }

  if (isTransportError) {
    return createApiError(
      "API momentan nicht erreichbar. Bitte Seite neu laden und erneut versuchen.",
      { attemptedOrigins }
    );
  }

  return createApiError(message, { attemptedOrigins });
};

const shouldTryNextOrigin = (error) => {
  if (!error) {
    return false;
  }

  const message = error.message || "";
  return (
    message.includes("(0)") ||
    message.includes("Failed to fetch") ||
    message.includes("Network request failed") ||
    message.includes("CORS")
  );
};

const xhrJsonRequest = (url, { method = "GET", headers = {}, body = null } = {}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== XMLHttpRequest.DONE) {
        return;
      }

      const responseText = xhr.responseText || "";

      if (xhr.status >= 200 && xhr.status < 300) {
        // Wrap in data property for consistent access in requestApiJson
        resolve({ data: parseJsonSafely(responseText) });
        return;
      }

      const errorData = parseJsonSafely(responseText);
      reject(
        createApiError(
          errorData?.detail || `Request fehlgeschlagen (${xhr.status})`,
          { status: xhr.status, responseText }
        )
      );
    };

    xhr.onerror = () => reject(createApiError("Request fehlgeschlagen (0)", { status: 0 }));
    xhr.send(body);
  });

const fetchWithFallback = async (path, executeRequest) => {
  const attemptedOrigins = [];
  let lastError = null;

  for (const origin of getOriginCandidates()) {
    attemptedOrigins.push(origin);

    try {
      const result = await executeRequest(origin);
      storeResolvedOrigin(origin);
      return {
        ...result,
        origin,
        attemptedOrigins,
      };
    } catch (error) {
      lastError = error;
      if (!shouldTryNextOrigin(error)) {
        break;
      }
    }
  }

  throw createFriendlyApiError(lastError, attemptedOrigins);
};

export const getApiBase = () => {
  const currentOrigin = getWindowOrigin();
  if (currentOrigin && currentOrigin !== configuredOrigin) {
    return `${currentOrigin}/api`;
  }

  return `${readStoredOrigin() || currentOrigin || configuredOrigin}/api`;
};

export const getApiStatusSnapshot = () => {
  const currentOrigin = getWindowOrigin();
  const activeOrigin = readStoredOrigin() || currentOrigin || configuredOrigin;

  return {
    configuredOrigin,
    currentOrigin,
    activeOrigin,
    isUsingSameOrigin: !!currentOrigin && activeOrigin === currentOrigin,
    isUsingConfiguredOrigin: activeOrigin === configuredOrigin,
  };
};

export const ensureApiAvailable = async () => {
  const result = await fetchWithFallback(API_HEALTH_PATH, (origin) =>
    xhrJsonRequest(buildApiUrl(origin, API_HEALTH_PATH))
  );

  return {
    ...getApiStatusSnapshot(),
    state: "ready",
    message:
      result.origin === configuredOrigin
        ? "API-Verbindung aktiv"
        : "API-Verbindung über aktuelle Domain aktiv",
  };
};

export const requestApiJson = async (path, options = {}) => {
  const result = await fetchWithFallback(path, (origin) =>
    xhrJsonRequest(buildApiUrl(origin, path), options)
  );

  return result.data;
};

export const fetchApi = async (path, options = {}) => {
  const result = await fetchWithFallback(path, async (origin) => {
    const response = await window.fetch(buildApiUrl(origin, path), options);
    if (!response.ok) {
      const responseText = await readResponseTextSafely(response);
      const errorData = parseJsonSafely(responseText);
      throw createApiError(
        errorData?.detail || response.statusText || `Request fehlgeschlagen (${response.status})`,
        { status: response.status, responseText }
      );
    }

    return { data: response };
  });

  return result.data;
};

export const getFriendlyApiMessage = (error) =>
  createFriendlyApiError(error).message;